import { CountryCheck } from "@/components/CountryCheck"
import { ErrorPage } from "@/components/ErrorPage/ErrorPage"
import { CampaignPopup } from "@/containers/CampaignPopup/CampaignPopup"
import { theme } from "@/layout/theme"
import { useLayout } from "@/lib/preFetch/preFetchLayout"
import { fetchPopup } from "@/lib/prismicio/popup"
import { useEmailValidation } from "@/lib/prismicio/useEmailValidation"
import { useWishlist } from "@/lib/store/wishlist"
import {
    AddedProductProvider,
    I18nCtx,
    LayoutErrorBoundary,
    PopupProvider,
    ValidateEmail,
    useAdpage,
    useSovendus,
    useTwProfileKey,
} from "@jog/react-components"
import { useAwin } from "@jog/react-components/src/hooks/useAwin"
import {
    NewsletterObject,
    gtmAddUserId,
    gtmAdpageUserData,
    gtmPageTracking,
    localStorage,
    shouldShowPopupModal,
} from "@jog/shared"
import { ThemeProvider, withTheme } from "@jog/theming"
import { AlertBox, Footer, Header, Modal, TopBanner } from "components"
import { useRouter } from "next/router"
import React, { PropsWithChildren, useCallback, useEffect, useState } from "react"
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3"

const reCaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_CLIENT_KEY
const LayoutError = () => <ErrorPage />

export function Layout({
    children,
    isCheckoutPage = false,
    newsletter = true,
    isTopBannerVisible = true,
    gtmTypeOfPage = "",
    isCartPage = false,
}: PropsWithChildren<{
    children?: React.ReactNode
    newsletter?: boolean
    isTopBannerVisible?: boolean
    isCartPage?: boolean
    gtmTypeOfPage?: string
    isCheckoutPage?: boolean
}>) {
    const [showPopupModal, setShowPopupModal] = useState(false)

    const router = useRouter()
    useTwProfileKey()

    useEffect(() => {
        const navigationLanguage = localStorage.getItem("navigationLanguage")
        const lang = process.env.NEXT_PUBLIC_LANG

        if (!navigationLanguage || navigationLanguage !== lang) {
            localStorage.setItem("navigationLanguage", lang)
        }
    }, [])

    useEffect(() => {
        if (router.isReady) {
            gtmPageTracking(gtmTypeOfPage)
        }
    }, [gtmTypeOfPage, router])
    const adpage = useAdpage()
    useEffect(() => {
        if (router.isReady && adpage?.marketing && adpage?.device) {
            gtmAdpageUserData(adpage, gtmTypeOfPage)
        }
    }, [adpage, gtmTypeOfPage, router.asPath, router.isReady])
    useEffect(() => {
        const userId = localStorage.getItem("user-id")
        gtmAddUserId(userId)
    }, [])

    const newsletterStorage = localStorage.getItem<NewsletterObject>("newsletter")

    const layout = useLayout()
    const validateEmailList = useEmailValidation()
    const wishlistError = useWishlist.use.error()
    const wishlist = useWishlist.use.initWishlist()
    useEffect(() => {
        wishlist()
    }, [wishlist])
    useAwin()
    const onClose = useCallback(() => {
        setShowPopupModal(shouldShowPopupModal())
    }, [])
    useSovendus()
    return (
        <AddedProductProvider closeTime={layout?.layout.cartPopupShowTime || 5000}>
            <PopupProvider fetch={fetchPopup}>
                <I18nCtx.Provider value={{ translations: layout?.translations || {} }}>
                    <ValidateEmail.Provider value={validateEmailList}>
                        <GoogleReCaptchaProvider
                            scriptProps={{ defer: true }}
                            reCaptchaKey={reCaptchaKey}
                            language={process.env.NEXT_PUBLIC_LANG}>
                            <ThemeProvider theme={theme}>
                                <Header isCartPage={isCartPage} isCheckoutPage={isCheckoutPage} />
                                {isTopBannerVisible && <TopBanner />}
                                <LayoutErrorBoundary Fallback={LayoutError}>{children}</LayoutErrorBoundary>
                                <Footer newsletter={newsletter} />
                                <CountryCheck onClose={onClose} />
                                {showPopupModal && !newsletterStorage?.isSubscribed && <CampaignPopup />}

                                {wishlistError && (
                                    <Modal>
                                        <AlertBox type="error" text={wishlistError.message} />
                                    </Modal>
                                )}
                            </ThemeProvider>
                        </GoogleReCaptchaProvider>
                    </ValidateEmail.Provider>
                </I18nCtx.Provider>
            </PopupProvider>
        </AddedProductProvider>
    )
}
export default withTheme(Layout)
